import { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { SERVICE_CATEGORIES, BUSINESS } from "../data/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-ink/15 bg-paper px-4 py-3 text-sm text-body placeholder:text-mute/70 transition-colors focus:border-copper";
const labelCls = "block text-xs font-semibold uppercase tracking-[0.14em] text-body/70 mb-2";

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}</label>
    {children}
    {error && (
      <p role="alert" className="mt-1.5 text-xs text-red-700 font-medium" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

const serviceOptions = SERVICE_CATEGORIES.flatMap((c) => c.services.map((s) => s.name));

function useForm(endpoint) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", preferred_date: "", preferred_time: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[+0-9][0-9\s-]{7,14}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.service) e.service = "Please choose a service.";
    if (!form.preferred_date) e.preferred_date = "Please pick a preferred date.";
    if (!form.preferred_time) e.preferred_time = "Please pick a preferred time.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/${endpoint}`, payload);
      setStatus("success");
      setForm({ name: "", phone: "", email: "", service: "", preferred_date: "", preferred_time: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return { form, set, errors, status, submit, setStatus };
}

const StatusMessage = ({ status, testId }) => {
  if (status === "success")
    return (
      <p role="status" data-testid={`${testId}-success`} className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-700/30 text-green-800 text-sm font-medium px-4 py-3">
        <CheckCircle2 size={17} aria-hidden="true" /> Thank you. Your enquiry has been received.
      </p>
    );
  if (status === "error")
    return (
      <p role="alert" data-testid={`${testId}-error`} className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-700/30 text-red-800 text-sm font-medium px-4 py-3">
        <AlertCircle size={17} aria-hidden="true" /> Something went wrong. Please try again or call us directly on {BUSINESS.phoneDisplay}.
      </p>
    );
  return null;
};

const SubmitButton = ({ status, label, testId }) => (
  <button
    type="submit"
    disabled={status === "loading"}
    data-testid={testId}
    className="w-full rounded-full bg-copper text-ink font-display font-bold px-8 py-4 transition-all duration-300 hover:bg-copper-hover disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
  >
    {status === "loading" && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
    {status === "loading" ? "Sending…" : label}
  </button>
);

const TimeDateRow = ({ form, set, errors }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <Field id="preferred_date" label="Preferred Date" error={errors.preferred_date}>
      <input id="preferred_date" data-testid="form-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} min={new Date().toISOString().split("T")[0]} />
    </Field>
    <Field id="preferred_time" label="Preferred Time" error={errors.preferred_time}>
      <select id="preferred_time" data-testid="form-time" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls}>
        <option value="">Select a time</option>
        {["Morning", "Afternoon", "Evening"].map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    </Field>
  </div>
);

const NamePhoneRow = ({ form, set, errors }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <Field id="name" label="Name *" error={errors.name}>
      <input id="name" data-testid="form-name" type="text" autoComplete="name" placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} required />
    </Field>
    <Field id="phone" label="Phone *" error={errors.phone}>
      <input id="phone" data-testid="form-phone" type="tel" autoComplete="tel" placeholder="+91 …" value={form.phone} onChange={set("phone")} className={inputCls} required />
    </Field>
  </div>
);

const ServiceField = ({ form, set, errors }) => (
  <Field id="service" label="Service *" error={errors.service}>
    <select id="service" data-testid="form-service" value={form.service} onChange={set("service")} className={inputCls} required>
      <option value="">Select a service</option>
      {serviceOptions.map((s) => <option key={s} value={s}>{s.replace(" [CONFIRM SERVICE]", "")}</option>)}
    </select>
  </Field>
);

export const BookingForm = () => {
  const f = useForm("appointments");
  return (
    <form onSubmit={f.submit} noValidate className="space-y-5" data-testid="booking-form">
      <NamePhoneRow {...f} />
      <ServiceField {...f} />
      <TimeDateRow {...f} />
      <Field id="message" label="Message (optional)">
        <textarea id="message" data-testid="form-message" rows={4} placeholder="Anything we should know?" value={f.form.message} onChange={f.set("message")} className={inputCls} />
      </Field>
      <StatusMessage status={f.status} testId="booking" />
      <SubmitButton status={f.status} label="Request Appointment" testId="booking-submit-btn" />
      <p className="text-xs text-mute text-center">
        Your requested time is not confirmed until the salon responds.
      </p>
    </form>
  );
};

export const ContactForm = () => {
  const f = useForm("enquiries");
  return (
    <form onSubmit={f.submit} noValidate className="space-y-5" data-testid="contact-form">
      <NamePhoneRow {...f} />
      <Field id="email" label="Email (optional)" error={f.errors.email}>
        <input id="email" data-testid="form-email" type="email" autoComplete="email" placeholder="you@example.com" value={f.form.email} onChange={f.set("email")} className={inputCls} />
      </Field>
      <ServiceField {...f} />
      <TimeDateRow {...f} />
      <Field id="message" label="Message">
        <textarea id="message" data-testid="form-message" rows={4} placeholder="How can we help?" value={f.form.message} onChange={f.set("message")} className={inputCls} />
      </Field>
      <StatusMessage status={f.status} testId="contact" />
      <SubmitButton status={f.status} label="Send Enquiry" testId="contact-submit-btn" />
    </form>
  );
};
